import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import WalletConnect from './components/WalletConnect';
import WalletModal from './components/WalletModal';
import Ticker from './components/Ticker';
import Leaderboard from './components/Leaderboard';
import TutorialOverlay from './components/TutorialOverlay';
import GamePage from './components/GamePage';
import LiquidSandBackdrop from './components/LiquidSandBackdrop';
import AchievementsPanel from './components/AchievementsPanel';
import DailyChallengeCard from './components/DailyChallenge';
import { useTutorial } from './hooks/useTutorial';
import { useDailyChallenge } from './hooks/useDailyChallenge';
import { useSiweAuth } from './hooks/useSiweAuth';
import { useAchievementSync } from './hooks/useAchievementSync';

const MainApp: React.FC = () => {
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [userTier] = useState<'FREE' | 'PREMIUM' | 'VIP'>('FREE');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [footerLogoVisible, setFooterLogoVisible] = useState(false);
  const footerLogoRef = useRef<HTMLImageElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    tutorialState,
    showTutorial,
    startTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    shouldShowTutorial,
    canSkipTutorial,
    setShowTutorial,
  } = useTutorial();

  const { challenge } = useDailyChallenge();

  // Durable, wallet-keyed achievement persistence. SIWE proves wallet control;
  // the sync hook reconciles the local cache with the server-side record so
  // achievements carry over across sessions and devices.
  const { authedAddress, isAuthed, canSignIn, status: authStatus, signIn } = useSiweAuth();
  useAchievementSync(authedAddress);

  // Prompt to sign in once per connection so progress starts syncing without a
  // manual step. If the user declines, the navbar still offers a manual button.
  const autoSignInTried = useRef(false);
  useEffect(() => {
    if (!canSignIn) {
      autoSignInTried.current = false;
      return;
    }
    if (authStatus === 'idle' && !autoSignInTried.current) {
      autoSignInTried.current = true;
      signIn();
    }
  }, [canSignIn, authStatus, signIn]);

  useEffect(() => {
    if (shouldShowTutorial()) {
      const timer = setTimeout(() => {
        setShowTutorial(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setFooterLogoVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (footerLogoRef.current) {
      observer.observe(footerLogoRef.current);
    }

    return () => {
      if (footerLogoRef.current) {
        observer.unobserve(footerLogoRef.current);
      }
    };
  }, []);

  const handleWalletConnect = useCallback(() => {
    setIsWalletConnected(true);
  }, []);

  const handleWalletDisconnect = useCallback(() => {
    setIsWalletConnected(false);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleTierClick = (tier: 'FREE' | 'PREMIUM' | 'VIP') => {
    // Require tutorial completion before allowing gameplay
    if (!tutorialState.tutorialCompleted) {
      setShowTutorial(true);
      return;
    }

    if (!isWalletConnected) {
      setShowWalletModal(true);
    } else {
      // Wallet is connected and tutorial is completed - navigate to game!
      if (tier !== 'FREE') {
        handleUpgrade(tier);
      }
      navigate('/game');
    }
  };

  const handleUpgrade = (tier: 'PREMIUM' | 'VIP') => {
    // TODO: Implement Coinbase payment flow
    console.log(`Upgrading to ${tier}`);
    alert(`Payment flow for ${tier} upgrade would be implemented here`);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const isHomePage = location.pathname === '/';

  return (
    <div className="App">
      <LiquidSandBackdrop />
      <header className="App-header">
        <div
          className="header-wordmark clickable-logo"
          onClick={handleLogoClick}
          role="button"
          aria-label="Histocracy home"
        >
          <span className="wordmark-text" data-text="HISTOCRACY">HISTOCRACY</span>
        </div>
        <div className={`hamburger-menu ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu}>
          <div className="hamburger-line"></div>
          <div className="hamburger-line"></div>
          <div className="hamburger-line"></div>
        </div>
        <div className={`header-actions ${isMenuOpen ? 'mobile-menu-open' : ''}`}>
          <button
            onClick={() => navigate('/achievements')}
            className="tutorial-nav-btn"
            title="Achievements"
          >
            <span className="tutorial-text">Achievements</span>
          </button>
          <button
            onClick={startTutorial}
            className="tutorial-nav-btn"
            title="View Tutorial"
          >
            <span className="tutorial-text">Tutorial</span>
          </button>
          {isWalletConnected && (
            <div className="sync-status" title="Achievement sync status">
              {authStatus === 'authenticating' ? (
                <span className="sync-pill sync-syncing">Syncing…</span>
              ) : isAuthed ? (
                <span className="sync-pill sync-on">Synced ✓</span>
              ) : (
                <button
                  type="button"
                  className="sync-pill sync-action"
                  onClick={signIn}
                  disabled={!canSignIn}
                >
                  Sync achievements
                </button>
              )}
            </div>
          )}
          <WalletConnect
            onConnect={handleWalletConnect}
            onDisconnect={handleWalletDisconnect}
          />
        </div>
      </header>

      <Ticker />

      <main className="App-main">
        <Routes>
          <Route path="/" element={
            <div className="welcome">
              <div className="hero-section">
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <h1 className="hero-title metallic-title fade-in">Nefarian Contrarian</h1>
                  <div className="hero-title-glow"></div>
                </div>
                <h2 className="hero-subtitle fade-in">Welcome to Heritage Ventures' <b>Histocracy</b> Trivia Challenge</h2>
              </div>

              <div className="tiers">
                <div className="tier tier-free" onClick={() => handleTierClick('FREE')}>
                  <span className="tier-name">Free</span>
                  <span className="tier-subtitle">Access Tier</span>
                  <div className="tier-hover-image">
                    <img
                      src={require('./assets/images/free.png')}
                      alt="Free tier"
                    />
                  </div>
                  <div className="tier-hover-content">
                    <button className="tier-cta-hover">Begin</button>
                  </div>
                </div>

                <div className="tier tier-premium" onClick={() => handleTierClick('PREMIUM')}>
                  <span className="tier-name">Premium</span>
                  <span className="tier-subtitle">Access Tier</span>
                  <div className="tier-hover-image">
                    <img
                      src={require('./assets/images/premium.png')}
                      alt="Premium tier"
                    />
                  </div>
                  <div className="tier-hover-content">
                    <button className="tier-cta-hover">Upgrade</button>
                  </div>
                </div>

                <div className="tier tier-vip" onClick={() => handleTierClick('VIP')}>
                  <span className="tier-name">VIP</span>
                  <span className="tier-subtitle">Access Tier</span>
                  <div className="tier-hover-image">
                    <img
                      src={require('./assets/images/vip.png')}
                      alt="VIP tier"
                    />
                  </div>
                  <div className="tier-hover-content">
                    <button className="tier-cta-hover">Go VIP</button>
                  </div>
                </div>
              </div>

              <DailyChallengeCard />

              <Leaderboard isFullPage={false} showTopN={5} />
            </div>
          } />
          <Route path="/game" element={<GamePage userTier={userTier} onUpgrade={handleUpgrade} gameMode="STANDARD" authedAddress={authedAddress} />} />
          <Route
            path="/daily"
            element={
              <GamePage
                userTier={userTier}
                onUpgrade={handleUpgrade}
                gameMode="DAILY_CHALLENGE"
                dailyQuestionIds={challenge.questionIds}
                authedAddress={authedAddress}
              />
            }
          />
          <Route path="/achievements" element={<AchievementsPanel />} />
          <Route path="/leaderboard" element={<Leaderboard isFullPage={true} />} />
        </Routes>
      </main>

      <footer className="App-footer">
        <div className="footer-content">
          <div className="footer-section">
            <img
              ref={footerLogoRef}
              src={require('./assets/images/hv-abstract-logo.png')}
              alt="Heritage Ventures Logo"
              className={`footer-logo footer-logo-animate ${footerLogoVisible ? 'visible' : ''}`}
            />
          </div>
          <div className="footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="#about">About</a></li>
              <li><a href="#services">Services</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Follow Us</h4>
            <div className="social-links">
              <span className="coming-soon-text">Coming Soon</span>
            </div>
          </div>
          <div className="footer-section">
            <h4>Visit Our Main Site</h4>
            <a href="https://www.heritageventures.io" target="_blank" rel="noopener noreferrer" className="heritage-link">
              Heritage Ventures
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025-2026 Heritage Ventures</p>
        </div>
      </footer>

      <WalletModal
        isOpen={showWalletModal}
        onClose={() => setShowWalletModal(false)}
        onConnect={handleWalletConnect}
      />

      {showTutorial && (
        <TutorialOverlay
          currentStep={tutorialState.currentStep}
          onNext={nextStep}
          onPrevious={previousStep}
          onSkip={skipTutorial}
          onComplete={completeTutorial}
          canSkip={canSkipTutorial()}
          isFirstTime={tutorialState.isFirstTime}
        />
      )}

      {!tutorialState.tutorialCompleted && !tutorialState.isFirstTime && !showTutorial && isHomePage && (
        <div className="tutorial-prompt">
          <div className="tutorial-prompt-content">
            <span>&#128075; Want a quick tour?</span>
            <button onClick={startTutorial} className="tutorial-prompt-btn">
              Start Tutorial
            </button>
            <button onClick={skipTutorial} className="tutorial-prompt-dismiss">
              &#10005;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        {/* All routes render inside MainApp so every page keeps the shared
            header, ticker and footer. (/leaderboard is handled by MainApp's
            own <Routes>.) */}
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </Router>
  );
}

export default App;
